class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    const debouncedOnQuantityChange = debounce((event) => this.onQuantityChange(event), ON_CHANGE_DEBOUNCE_TIMER);
    this.addEventListener('change', debouncedOnQuantityChange.bind(this));
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  // `parsedState.sections` (the /cart/add.js response) can be rendered from
  // the cart as it existed just before this add committed, which under-counts
  // cart-icon-bubble by one. Re-fetch the sections fresh via GET instead of
  // trusting the bundled response — same fix cart.js's onCartUpdate already
  // applies for cart-items/cart-drawer-items.
  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;

    const sectionsToRender = this.getSectionsToRender();
    const sectionIds = sectionsToRender.map((section) => section.id).join(',');

    return fetch(`${routes.cart_url}?sections=${sectionIds}`)
      .then((response) => response.json())
      .then((sections) => {
        sectionsToRender.forEach((section) => {
          const html = sections[section.id];
          if (!html) return;
          document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(html, section.selector);
        });

        if (this.header) this.header.reveal();
        this.open();
      })
      .catch((e) => console.error(e));
  }

  onQuantityChange(event) {
    const input = event.target;
    const lineKey = input?.dataset?.quantityLineKey;
    if (!lineKey) return;

    this.updateQuantity(lineKey, parseInt(input.value, 10), input);
  }

  updateQuantity(lineKey, quantity, input) {
    const sectionsToRender = this.getSectionsToRender();

    input.disabled = true;

    const body = JSON.stringify({
      id: lineKey,
      quantity,
      sections: sectionsToRender.map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.errors) {
          input.value = input.getAttribute('value');
          return;
        }

        sectionsToRender.forEach((section) => {
          const html = parsedState.sections[section.id];
          if (!html) return;
          const target =
            document.getElementById(section.id)?.querySelector(section.selector) ||
            document.getElementById(section.id);
          if (target) target.innerHTML = this.getSectionInnerHTML(html, section.selector);
        });

        if (this.header) this.header.reveal();

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-notification', cartData: parsedState });
      })
      .catch((e) => console.error(e))
      .finally(() => {
        if (input.isConnected) input.disabled = false;
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (!this.notification.contains(target)) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
